// library imports
import { useEffect, useState, useContext } from "react";
// hook imports
import useFetch from "../../../hooks/useFetch";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// config imports
import { MQTT_REFRESH_RATE } from "../../../configs/configs";
// component import
import WeatherStationContent from "./WeatherStationContent";

function WeatherStation({ devicePhaseNumberList }) {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  // phase number selected
  const [phaseNumberSelected, setPhaseNumberSelected] = useState(null);

  // device list for weather station
  const [deviceList, setDeviceList] = useState([]);

  const [selectedDeviceID, setSelectedDeviceID] = useState();
  const [weatherStationSystemLoading, setWeatherStationSystemLoading] =
    useState(true);
  const [weatherStationDeviceLoading, setWeatherStationDeviceLoading] =
    useState(false);
  const [weatherStationSystemData, setWeatherStationSystemData] = useState({});
  const [weatherStationTableData, setWeatherStationTableData] = useState([]);
  const [deviceSelectionLoading, setDeviceSelectionLoading] = useState(false);

  const [systemLastUpdate, setSystemLastUpdate] = useState("-");
  const [deviceLastUpdate, setDeviceLastUpdate] = useState("-");

  /** API to get device name for the device selection */
  function getDeviceName() {
    setDeviceSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "system/getDeviceName",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        phase_number: phaseNumberSelected,
      },
      onSuccess: (response) => {
        setDeviceList(response.data?.WEATHER ?? []);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device name for Weather Station system page.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setDeviceSelectionLoading(false);
      },
    });
  }

  /** API to get WeatherStation device data */
  function getWeatherStationDeviceData() {
    fetchWrapper({
      endpoint_url: "system/getWeatherStationDeviceData",
      method: "GET",
      set_content_type_header: false,
      params: {
        device_id: selectedDeviceID,
      },

      onSuccess: (response) => {
        setWeatherStationTableData(response.data.table);
        setDeviceLastUpdate(response.data.last_update);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get Weather Station device data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setWeatherStationDeviceLoading(false);
      },
    });
  }

  /** API to get Weather Station device data */
  function getWeatherStationSystemData() {
    fetchWrapper({
      endpoint_url: "system/getSystemWeatherStation",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setWeatherStationSystemData(response.data.system);
        setSystemLastUpdate(response.data.last_update);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get Weather Station system data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setWeatherStationSystemLoading(false);
      },
    });
  }

  useEffect(() => {
    if (deviceList && deviceList.length > 0) {
      setSelectedDeviceID(deviceList[0].value);
    } else {
      setSelectedDeviceID(null);
      setWeatherStationTableData([]);
    }
  }, [deviceList]);

  useEffect(() => {
    getWeatherStationSystemData();

    const timer = setInterval(() => {
      if (siteSelected) {
        getWeatherStationSystemData();
      }
    }, MQTT_REFRESH_RATE * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [siteSelected]);

  useEffect(() => {
    if (selectedDeviceID) {
      setWeatherStationDeviceLoading(true);
      getWeatherStationDeviceData();
    }

    const timer = setInterval(() => {
      if (selectedDeviceID) {
        getWeatherStationDeviceData();
      }
    }, MQTT_REFRESH_RATE * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [selectedDeviceID]);

  useEffect(() => {
    if (devicePhaseNumberList && devicePhaseNumberList.length > 0) {
      setPhaseNumberSelected(devicePhaseNumberList[0].key);
    }
  }, [devicePhaseNumberList]);

  useEffect(() => {
    if (phaseNumberSelected && siteSelected) {
      getDeviceName();
    }
  }, [phaseNumberSelected, siteSelected]);

  return (
    <>
      <WeatherStationContent
        devicePhaseNumberList={devicePhaseNumberList}
        deviceList={deviceList}
        systemData={weatherStationSystemData}
        tableData={weatherStationTableData}
        handleDeviceChange={(key) => {
          setSelectedDeviceID(key);
          setWeatherStationDeviceLoading(true);
        }}
        weatherStationSystemLoading={weatherStationSystemLoading}
        weatherStationDeviceLoading={weatherStationDeviceLoading}
        deviceSelectionLoading={deviceSelectionLoading}
        selectedDeviceID={selectedDeviceID}
        systemLastUpdate={systemLastUpdate}
        deviceLastUpdate={deviceLastUpdate}
        setPhaseNumberSelected={setPhaseNumberSelected}
      />
    </>
  );
}

export default WeatherStation;
